import { Injectable, signal, inject, Injector } from '@angular/core';
import { User, AuthUser, UserRole } from '../models/user.model';
import type { WhoamiPayload } from '../models/whoami.model';
import { environment } from '../environments/environment';
import { StorageEncryptionService } from './storage-encryption.service';
import { SessionCleanupService } from './session-cleanup.service';

interface AuthResponse {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: User;
  isAdmin?: boolean;
  error?: string;
  message?: string;
  retryAfter?: number; // Rate limit retry after (in seconds)
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Current user signal
  currentUser = signal<AuthUser | null>(null);
  currentUserProfile = signal<User | null>(null);
  authToken = signal<string | null>(null);
  isInitialized = signal<boolean>(false);

  /**
   * Server identity contract (GET /v1/auth/whoami). Shell UX only — do not duplicate or infer.
   */
  whoamiContext = signal<WhoamiPayload | null>(null);

  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_DATA_KEY = 'user_data'; // Store minimal user data for quick access
  private readonly REMEMBER_EMAIL_KEY = 'remember_email_encrypted';
  private readonly REMEMBER_PASSWORD_KEY = 'remember_password_encrypted';
  private readonly REMEMBER_ME_KEY = 'remember_me';
  private readonly TOKEN_ENCRYPTED_FLAG = 'auth_token_encrypted'; // Flag to indicate encrypted storage
  /** Screen dimensions (WxH) at encryption time — legacy decrypt after DevTools viewport change. */
  private readonly ENCRYPTION_SCREEN_KEY = 'auth_encryption_screen';
  /** Set when tokens use viewport-independent encryption (v2 key derivation). */
  private readonly ENCRYPTION_V2_KEY = 'auth_encryption_v2';
  private readonly RATE_LIMIT_KEY = 'auth_rate_limit_until'; // Timestamp when rate limit expires
  /** Set on explicit logout so reload does not silent auto-login with Remember Me credentials. */
  private readonly EXPLICIT_LOGOUT_KEY = 'auth_explicit_logout';
  private readonly PASSWORD_RESET_COOLDOWN_KEY = 'password_reset_cooldown_until';
  private readonly PASSWORD_RESET_COOLDOWN_MS = 60_000;
  private tokenRefreshPromise: Promise<boolean> | null = null;
  private loginPromise: Promise<AuthUser> | null = null; // Prevent multiple simultaneous login attempts
  
  private encryptionService = inject(StorageEncryptionService);
  private injector = inject(Injector);
  private isHandlingExternalLogout = false;

  constructor() {
    // Initialize auth state on service creation
    this.initializeAuth();
    this.setupStorageListener();
  }

  private setupStorageListener(): void {
    if (typeof window === 'undefined' || !window.addEventListener) {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this.TOKEN_KEY || event.newValue !== null) {
        return;
      }

      if (this.isHandlingExternalLogout) {
        return;
      }

      // If another tab cleared the token, clear local auth state here too.
      if (this.authToken() || this.currentUser() || this.currentUserProfile()) {
        this.isHandlingExternalLogout = true;
        this.logout(true)
          .catch(() => {
            // Ignore errors; we're already logged out in another tab.
          })
          .finally(() => {
            this.isHandlingExternalLogout = false;
            // Replace URL to prevent navigating back into protected pages
            window.location.replace('/auth/login');
          });
      }
    });
  }

  /**
   * Initialize authentication state from storage
   * This is called on service creation and should complete before guards check auth
   */
  async initializeAuth(): Promise<void> {
    try {
      const savedToken = await this.getTokenSecurely();
      
      if (!savedToken) {
        // No session token: stay logged out. Remember Me only prefills the login form —
        // it must not silently re-authenticate after the user logged out (or on a fresh visit).
        this.isInitialized.set(true);
        return;
      }

      // Set token immediately for guards
      this.authToken.set(savedToken);

      // Try to load user profile - this validates the token
      try {
        await this.loadUserProfile();
        // If successful, we're authenticated
        localStorage.removeItem(this.EXPLICIT_LOGOUT_KEY);
        this.isInitialized.set(true);
        return;
      } catch (profileError) {
        // Token present but invalid/expired — recover via Remember Me only if user did not explicitly log out
        console.warn('Token validation failed, attempting auto-login:', profileError);
        
        if (this.isRememberMeEnabled() && !this.wasExplicitLogout()) {
          // Clear invalid token but keep credentials
          localStorage.removeItem(this.TOKEN_KEY);
          localStorage.removeItem(this.REFRESH_TOKEN_KEY);
          localStorage.removeItem(this.TOKEN_ENCRYPTED_FLAG);
          this.authToken.set(null);
          
          // Attempt auto-login with stored credentials
          const autoLoginSuccess = await this.attemptAutoLogin();
          if (autoLoginSuccess) {
            localStorage.removeItem(this.EXPLICIT_LOGOUT_KEY);
            this.isInitialized.set(true);
            return;
          }
        }
        
        // Auto-login failed or not allowed — clear session tokens only
        await this.logout(false);
        this.isInitialized.set(true);
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      // Do not silent-login after unexpected init errors if user logged out
      if (this.isRememberMeEnabled() && !this.wasExplicitLogout()) {
        const autoLoginSuccess = await this.attemptAutoLogin();
        if (!autoLoginSuccess) {
          await this.logout(false);
        } else {
          localStorage.removeItem(this.EXPLICIT_LOGOUT_KEY);
        }
      } else {
        await this.logout(false);
      }
      this.isInitialized.set(true);
    }
  }

  /**
   * Check if we're currently rate limited
   * Returns the number of seconds until rate limit expires, or 0 if not rate limited
   */
  private isRateLimited(): number {
    const rateLimitUntil = localStorage.getItem(this.RATE_LIMIT_KEY);
    if (!rateLimitUntil) {
      return 0;
    }

    const until = parseInt(rateLimitUntil, 10);
    const now = Date.now();
    
    if (now >= until) {
      // Rate limit expired, clear it
      localStorage.removeItem(this.RATE_LIMIT_KEY);
      return 0;
    }

    // Return seconds until rate limit expires
    return Math.ceil((until - now) / 1000);
  }

  /**
   * Set rate limit based on retryAfter value from server
   */
  private setRateLimit(retryAfterSeconds: number): void {
    const until = Date.now() + (retryAfterSeconds * 1000);
    localStorage.setItem(this.RATE_LIMIT_KEY, until.toString());
  }

  /**
   * Attempt automatic login using stored credentials
   * Returns true if login was successful, false otherwise
   * This is a silent login that doesn't trigger UI updates until successful
   */
  private async attemptAutoLogin(): Promise<boolean> {
    // Check rate limit before attempting auto-login
    const rateLimitSeconds = this.isRateLimited();
    if (rateLimitSeconds > 0) {
      console.warn(`Auto-login skipped: rate limited for ${rateLimitSeconds} more seconds`);
      return false;
    }

    try {
      const credentials = await this.getStoredCredentials();
      
      if (!credentials || !credentials.email || !credentials.password) {
        return false;
      }

      // Perform login directly without going through the public login method
      // to avoid circular dependencies during initialization
      const response = await fetch(`${environment.apiBaseUrl}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: credentials.email, 
          password: credentials.password 
        }),
      });

      const data: AuthResponse = await response.json();

      // Handle rate limiting
      if (response.status === 429 || data.retryAfter) {
        const retryAfter = data.retryAfter || 60; // Default to 60 seconds if not provided
        this.setRateLimit(retryAfter);
        console.warn(`Auto-login rate limited. Retry after ${retryAfter} seconds`);
        return false;
      }

      if (!response.ok || !data.success || !data.token || !data.user) {
        throw new Error(data.error || 'Auto-login failed');
      }

      // Clear rate limit on successful login
      localStorage.removeItem(this.RATE_LIMIT_KEY);

      // Store token and refresh token securely (encrypted)
      await this.storeTokenSecurely(data.token);
      if (data.refreshToken) {
        await this.storeRefreshTokenSecurely(data.refreshToken);
      }
      this.authToken.set(data.token);

      // Set user data
      const authUser: AuthUser = {
        uid: data.user.uid || data.user.id || '',
        email: data.user.email,
        displayName: data.user.name,
        phoneNumber: data.user.phoneNumber || null,
        isAdmin: data.isAdmin || false,
      };

      this.currentUser.set(authUser);
      this.currentUserProfile.set(data.user);

      // Store minimal user data for quick access (encrypted)
      await this.storeUserDataSecurely(authUser);

      // Credentials are already stored (rememberMe was true), so no need to store again
      
      localStorage.removeItem(this.EXPLICIT_LOGOUT_KEY);
      void this.refreshWhoami();
      return true;
    } catch (error) {
      console.error('Auto-login failed:', error);
      // Clear invalid credentials only if it's an auth error
      if (error instanceof Error && (
        error.message.includes('Invalid email or password') || 
        error.message.includes('401') ||
        error.message.includes('Unauthorized')
      )) {
        this.clearStoredCredentials();
      }
      return false;
    }
  }

  /**
   * Wait for auth initialization to complete
   * Used by guards to ensure auth state is loaded
   */
  async waitForInitialization(): Promise<void> {
    // If already initialized, return immediately
    if (this.isInitialized()) {
      return;
    }

    // If we have user data and token, consider it initialized (performance optimization)
    if (this.currentUser() && this.currentUserProfile() && this.authToken()) {
      this.isInitialized.set(true);
      return;
    }

    // Wait for initialization to complete (max 2 seconds for faster navigation)
    const maxWait = 2000;
    const startTime = Date.now();
    
    while (!this.isInitialized() && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 50)); // Check more frequently (50ms)
    }

    if (!this.isInitialized()) {
      console.warn('Auth initialization timeout');
      this.isInitialized.set(true); // Set to true to prevent infinite waiting
    }
  }

  /**
   * Sign in with email and password
   * @param rememberMe - If true, stores encrypted credentials for auto-login
   */
  async login(email: string, password: string, rememberMe: boolean = false): Promise<AuthUser> {
    // Prevent multiple simultaneous login attempts
    if (this.loginPromise) {
      return this.loginPromise;
    }

    // Check rate limit before attempting login
    const rateLimitSeconds = this.isRateLimited();
    if (rateLimitSeconds > 0) {
      const error = new Error(`Too many login attempts. Please try again in ${rateLimitSeconds} seconds.`);
      error.name = 'RateLimitError';
      throw error;
    }

    this.loginPromise = (async () => {
      try {
        const response = await fetch(`${environment.apiBaseUrl}/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        const data: AuthResponse = await response.json();

        // Handle rate limiting
        if (response.status === 429 || data.retryAfter) {
          const retryAfter = data.retryAfter || 60; // Default to 60 seconds if not provided
          this.setRateLimit(retryAfter);
          const error = new Error(`Too many login attempts. Please try again in ${retryAfter} seconds.`);
          error.name = 'RateLimitError';
          throw error;
        }

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Login failed');
        }

        if (!data.token || !data.user) {
          throw new Error('Invalid response from server');
        }

        // Clear rate limit on successful login
        localStorage.removeItem(this.RATE_LIMIT_KEY);

        // Discard any stale session state from a prior user before setting new auth data
        this.getSessionCleanup().prepareForLogin();

        // Set token immediately for guards (performance optimization)
        this.authToken.set(data.token);

        // Set user data immediately (before storage operations)
        const authUser: AuthUser = {
          uid: data.user.uid || data.user.id || '',
          email: data.user.email,
          displayName: data.user.name,
          phoneNumber: data.user.phoneNumber || null,
          isAdmin: data.isAdmin || false,
        };

        this.currentUser.set(authUser);
        this.currentUserProfile.set(data.user);
        
        // Mark as initialized immediately after setting user data (performance optimization)
        // This allows guards to skip initialization wait
        this.isInitialized.set(true);
        localStorage.removeItem(this.EXPLICIT_LOGOUT_KEY);

        // Store data in background (non-blocking) for better performance
        // Navigation can happen immediately after user data is set
        Promise.all([
          this.storeTokenSecurely(data.token),
          data.refreshToken ? this.storeRefreshTokenSecurely(data.refreshToken) : Promise.resolve(),
          this.storeUserDataSecurely(authUser),
          rememberMe ? this.storeCredentials(email, password) : Promise.resolve()
        ]).catch(error => {
          console.error('Background storage operations failed:', error);
          // Don't throw - user is already logged in
        });

        if (!rememberMe) {
          // Clear stored credentials if rememberMe is disabled (synchronous)
          this.clearStoredCredentials();
        }

        void this.refreshWhoami();
        return authUser;
      } catch (error: any) {
        throw this.handleAuthError(error);
      } finally {
        // Clear login promise after completion
        this.loginPromise = null;
      }
    })();

    return this.loginPromise;
  }

  /**
   * Register new user with email and password
   */
  async register(email: string, password: string, name: string, phoneNumber?: string): Promise<AuthUser> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name,
          phoneNumber: phoneNumber || null,
        }),
      });

      const data: AuthResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Registration failed');
      }

      if (!data.token || !data.user) {
        throw new Error('Invalid response from server');
      }

      // Set token immediately for guards (performance optimization)
      this.authToken.set(data.token);

      // Set user data immediately (before storage operations)
      const authUser: AuthUser = {
        uid: data.user.uid || data.user.id || '',
        email: data.user.email,
        displayName: data.user.name,
        phoneNumber: data.user.phoneNumber || null,
        isAdmin: false, // New users are not admin by default
      };

      this.currentUser.set(authUser);
      this.currentUserProfile.set(data.user);

      // Store data in background (non-blocking) for better performance
      Promise.all([
        this.storeTokenSecurely(data.token),
        data.refreshToken ? this.storeRefreshTokenSecurely(data.refreshToken) : Promise.resolve(),
        this.storeUserDataSecurely(authUser)
      ]).catch(error => {
        console.error('Background storage operations failed:', error);
        // Don't throw - user is already registered
      });

      void this.refreshWhoami();
      return authUser;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   * Clears all auth session data. Stored Remember Me credentials remain for login form
   * prefilling unless clearCredentials is true — but reload will not auto-login.
   * @param clearCredentials - If true, also clears stored encrypted credentials and encryption salt
   */
  async logout(clearCredentials: boolean = false): Promise<void> {
    try {
      // Prevent silent re-auth on the next page load
      localStorage.setItem(this.EXPLICIT_LOGOUT_KEY, '1');

      // Clear all stored auth data
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_DATA_KEY);
      localStorage.removeItem(this.TOKEN_ENCRYPTED_FLAG);
      localStorage.removeItem(this.ENCRYPTION_SCREEN_KEY);
      localStorage.removeItem(this.ENCRYPTION_V2_KEY);
      
      // Clear signals
      this.currentUser.set(null);
      this.currentUserProfile.set(null);
      this.authToken.set(null);
      this.isInitialized.set(true);
      this.whoamiContext.set(null);

      // Clear company, module, API, and other session caches
      this.getSessionCleanup().clearSessionState(clearCredentials);

      // Clear stored credentials if requested
      if (clearCredentials) {
        this.clearStoredCredentials();
        // Also clear encryption salt from IndexedDB for maximum security
        await this.encryptionService.clearEncryptionData();
      }
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  private wasExplicitLogout(): boolean {
    return localStorage.getItem(this.EXPLICIT_LOGOUT_KEY) === '1';
  }

  private getEncryptionScreenHint(): string | null {
    return localStorage.getItem(this.ENCRYPTION_SCREEN_KEY);
  }

  private recordEncryptionScreen(): void {
    if (typeof screen !== 'undefined') {
      localStorage.setItem(this.ENCRYPTION_SCREEN_KEY, `${screen.width}x${screen.height}`);
    }
  }

  /**
   * Store encrypted credentials for "Remember Me" feature
   */
  private async storeCredentials(email: string, password: string): Promise<void> {
    try {
      this.recordEncryptionScreen();
      const encryptedEmail = await this.encryptionService.encrypt(email);
      const encryptedPassword = await this.encryptionService.encrypt(password);
      
      localStorage.setItem(this.REMEMBER_EMAIL_KEY, encryptedEmail);
      localStorage.setItem(this.REMEMBER_PASSWORD_KEY, encryptedPassword);
      localStorage.setItem(this.REMEMBER_ME_KEY, 'true');
    } catch (error) {
      console.error('Failed to store credentials:', error);
      // Don't throw - this is not critical for login
    }
  }

  /**
   * Clear stored credentials
   */
  clearStoredCredentials(): void {
    localStorage.removeItem(this.REMEMBER_EMAIL_KEY);
    localStorage.removeItem(this.REMEMBER_PASSWORD_KEY);
    localStorage.removeItem(this.REMEMBER_ME_KEY);
  }

  /**
   * Retrieve stored credentials (decrypted)
   * Returns null if credentials are not stored or decryption fails
   */
  async getStoredCredentials(): Promise<{ email: string; password: string } | null> {
    try {
      const rememberMe = localStorage.getItem(this.REMEMBER_ME_KEY);
      if (!rememberMe || rememberMe !== 'true') {
        return null;
      }

      const encryptedEmail = localStorage.getItem(this.REMEMBER_EMAIL_KEY);
      const encryptedPassword = localStorage.getItem(this.REMEMBER_PASSWORD_KEY);

      if (!encryptedEmail || !encryptedPassword) {
        return null;
      }

      let email: string;
      let password: string;

      try {
        email = await this.encryptionService.decrypt(encryptedEmail, this.getEncryptionScreenHint());
        password = await this.encryptionService.decrypt(encryptedPassword, this.getEncryptionScreenHint());
      } catch (decryptError) {
        // Decryption failed - credentials are invalid (maybe key changed, salt changed, or data corrupted)
        console.error('Failed to decrypt stored credentials, clearing them:', decryptError);
        this.clearStoredCredentials();
        return null;
      }

      // Validate decrypted values
      if (!email || !password || email.trim() === '' || password.trim() === '') {
        console.warn('Decrypted credentials are empty, clearing them');
        this.clearStoredCredentials();
        return null;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        console.warn('Decrypted email is invalid, clearing credentials');
        this.clearStoredCredentials();
        return null;
      }

      // Check if values are still encrypted (base64-like strings that are too long)
      // This can happen if decryption silently failed and returned the encrypted string
      const isLikelyEncrypted = (value: string) => {
        const base64Pattern = /^[A-Za-z0-9+/=]+$/;
        return base64Pattern.test(value) && value.length > 50 && !value.includes('@');
      };

      if (isLikelyEncrypted(email) || isLikelyEncrypted(password)) {
        console.warn('Decrypted values appear to still be encrypted, clearing credentials');
        this.clearStoredCredentials();
        return null;
      }

      return { email, password };
    } catch (error) {
      console.error('Failed to retrieve stored credentials:', error);
      // Clear credentials on any error to prevent showing encrypted values
      this.clearStoredCredentials();
      return null;
    }
  }

  /**
   * Check if "Remember Me" is enabled
   */
  isRememberMeEnabled(): boolean {
    return localStorage.getItem(this.REMEMBER_ME_KEY) === 'true';
  }

  /**
   * Remaining password-reset cooldown (seconds), or 0 if none.
   * Persists across refresh via localStorage.
   */
  getPasswordResetCooldownRemaining(): number {
    const raw = localStorage.getItem(this.PASSWORD_RESET_COOLDOWN_KEY);
    if (!raw) {
      return 0;
    }
    const until = parseInt(raw, 10);
    if (Number.isNaN(until) || Date.now() >= until) {
      localStorage.removeItem(this.PASSWORD_RESET_COOLDOWN_KEY);
      return 0;
    }
    return Math.ceil((until - Date.now()) / 1000);
  }

  private startPasswordResetCooldown(): void {
    localStorage.setItem(
      this.PASSWORD_RESET_COOLDOWN_KEY,
      String(Date.now() + this.PASSWORD_RESET_COOLDOWN_MS)
    );
  }

  /**
   * Send password reset email via backend only (never Supabase from the browser).
   */
  async resetPassword(email: string): Promise<void> {
    if (this.getPasswordResetCooldownRemaining() > 0) {
      const err = new Error('Password reset cooldown active');
      err.name = 'PasswordResetCooldownError';
      throw err;
    }

    let response: Response;
    try {
      response = await fetch(`${environment.apiBaseUrl}/v1/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
    } catch (error: unknown) {
      throw this.handlePasswordResetError(error);
    }

    let data: Partial<AuthResponse> = {};
    try {
      data = (await response.json()) as AuthResponse;
    } catch {
      if (!response.ok) {
        const err = new Error('Something went wrong');
        err.name = 'PasswordResetGenericError';
        throw err;
      }
    }

    if (response.status === 429) {
      const err = new Error(
        data.message || data.error || 'Too many attempts. Please wait a minute.'
      );
      err.name = 'PasswordResetRateLimitError';
      throw err;
    }

    if (!response.ok || data.success !== true) {
      const err = new Error(data.error || data.message || 'Something went wrong');
      err.name = 'PasswordResetGenericError';
      throw err;
    }

    this.startPasswordResetCooldown();
  }

  private handlePasswordResetError(error: unknown): Error {
    if (error instanceof Error && error.name === 'PasswordResetCooldownError') {
      return error;
    }
    if (error instanceof TypeError && String(error.message).toLowerCase().includes('fetch')) {
      const err = new Error('Network error. Please check your connection');
      err.name = 'PasswordResetGenericError';
      return err;
    }
    const err = new Error('Something went wrong');
    err.name = 'PasswordResetGenericError';
    return err;
  }

  /**
   * Get current auth token (decrypted from secure storage)
   */
  async getToken(): Promise<string | null> {
    if (this.authToken()) {
      return this.authToken();
    }
    return await this.getTokenSecurely();
  }

  /**
   * Fetch server identity contract (GET /v1/auth/whoami). Safe to call often; clears context if unauthenticated.
   */
  async refreshWhoami(): Promise<void> {
    try {
      const token = await this.getToken();
      if (!token) {
        this.whoamiContext.set(null);
        return;
      }
      const response = await fetch(`${environment.apiBaseUrl}/v1/auth/whoami`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        this.whoamiContext.set(null);
        return;
      }
      const data = (await response.json()) as WhoamiPayload;
      if (data.success) {
        this.whoamiContext.set(data);
      } else {
        this.whoamiContext.set(null);
      }
    } catch {
      this.whoamiContext.set(null);
    }
  }

  /**
   * End server-side impersonation overlay; reloads profile as the authenticated principal.
   */
  async exitImpersonation(): Promise<void> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    const response = await fetch(`${environment.apiBaseUrl}/v1/auth/impersonation/exit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await response.json().catch(() => ({}))) as { success?: boolean; error?: string };
    if (!response.ok || data.success !== true) {
      throw new Error(data.error || 'Failed to exit impersonation');
    }
    this.getSessionCleanup().clearSessionState(false);
    this.currentUser.set(null);
    this.currentUserProfile.set(null);
    await this.loadUserProfile({ forceRefresh: true });
  }

  /**
   * Load user profile from backend
   * Validates token and refreshes user data
   * Public method for guards to call
   * @throws Error if token is invalid or profile cannot be loaded
   */
  async loadUserProfile(options?: { forceRefresh?: boolean }): Promise<void> {
    const forceRefresh = options?.forceRefresh === true;
    // Performance optimization: Skip API call if profile is already loaded
    if (!forceRefresh && this.currentUser() && this.currentUserProfile()) {
      await this.refreshWhoami();
      return;
    }

    try {
      const token = await this.getToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${environment.apiBaseUrl}/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          const authUser: AuthUser = {
            uid: data.user.uid || data.user.id || '',
            email: data.user.email,
            displayName: data.user.name,
            phoneNumber: data.user.phoneNumber || null,
            isAdmin: data.isAdmin || false,
          };

          this.currentUser.set(authUser);
          this.currentUserProfile.set(data.user);

          // Store minimal user data securely (encrypted)
          await this.storeUserDataSecurely(authUser);
          await this.refreshWhoami();
        } else {
          // Invalid response, throw error so caller can handle
          throw new Error('Invalid response from server');
        }
      } else if (response.status === 401) {
        // Token expired or invalid - try refresh if available
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          // Refresh failed - auth state is already cleared by handleTokenRefreshFailure()
          // Throw error to trigger logout/redirect
          const error = new Error('Token expired and refresh failed. Please log in again.');
          error.name = 'TokenRefreshError';
          throw error;
        }
        // If refresh succeeded, retry loading profile
        return this.loadUserProfile();
      } else {
        // Other error, throw so caller can decide what to do
        throw new Error(`Failed to load profile: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Re-throw so caller can handle (e.g., attempt auto-login)
      throw error;
    }
  }

  /**
   * Public method to refresh authentication token
   * This can be called by API service when token expires
   */
  async refreshAuthToken(): Promise<boolean> {
    return this.refreshToken();
  }

  /**
   * Refresh authentication token using refresh token
   */
  private async refreshToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = (async () => {
      try {
        const refreshToken = await this.getRefreshTokenSecurely();
        if (!refreshToken) {
          console.warn('No refresh token available');
          // Clear auth state and trigger logout
          await this.handleTokenRefreshFailure();
          return false;
        }

        // Call the refresh token endpoint
        const response = await fetch(`${environment.apiBaseUrl}/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to refresh token' }));
          console.error('Token refresh failed:', error);
          // Clear auth state and trigger logout
          await this.handleTokenRefreshFailure();
          return false;
        }

        const data: AuthResponse = await response.json();

        if (!data.success || !data.token || !data.user) {
          console.error('Invalid refresh response:', data);
          // Clear auth state and trigger logout
          await this.handleTokenRefreshFailure();
          return false;
        }

        // Store new token and refresh token
        await this.storeTokenSecurely(data.token);
        if (data.refreshToken) {
          await this.storeRefreshTokenSecurely(data.refreshToken);
        }
        this.authToken.set(data.token);

        // Update user data
        const authUser: AuthUser = {
          uid: data.user.uid || data.user.id || '',
          email: data.user.email,
          displayName: data.user.name,
          phoneNumber: data.user.phoneNumber || null,
          isAdmin: data.isAdmin || false,
        };

        this.currentUser.set(authUser);
        this.currentUserProfile.set(data.user);
        await this.storeUserDataSecurely(authUser);
        void this.refreshWhoami();

        return true;
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Clear auth state and trigger logout
        await this.handleTokenRefreshFailure();
        return false;
      } finally {
        this.tokenRefreshPromise = null;
      }
    })();

    return this.tokenRefreshPromise;
  }

  /**
   * Handle token refresh failure by clearing auth state
   * This will trigger guards to redirect to login
   */
  private async handleTokenRefreshFailure(): Promise<void> {
    try {
      // Clear all auth tokens and user data
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_ENCRYPTED_FLAG);
      localStorage.removeItem(this.USER_DATA_KEY);

      // Clear signals
      this.authToken.set(null);
      this.currentUser.set(null);
      this.currentUserProfile.set(null);
      this.isInitialized.set(false);
      this.whoamiContext.set(null);

      this.getSessionCleanup().clearSessionState(false);

      console.warn('Token refresh failed - auth state cleared. Redirecting to login...');
      
      // Redirect to login page immediately
      // Use setTimeout to avoid navigation during active request
      setTimeout(() => {
        const currentPath = window.location.pathname;
        // Only redirect if not already on login page
        if (!currentPath.includes('/auth/login') && !currentPath.includes('/auth/register')) {
          window.location.href = '/auth/login?returnUrl=' + encodeURIComponent(currentPath);
        }
      }, 100);
    } catch (error) {
      console.error('Failed to clear auth state after token refresh failure:', error);
    }
  }

  /**
   * Store token securely (encrypted) in localStorage
   */
  private async storeTokenSecurely(token: string): Promise<void> {
    try {
      this.recordEncryptionScreen();
      const encryptedToken = await this.encryptionService.encrypt(token);
      localStorage.setItem(this.TOKEN_KEY, encryptedToken);
      localStorage.setItem(this.TOKEN_ENCRYPTED_FLAG, 'true');
      localStorage.setItem(this.ENCRYPTION_V2_KEY, '1');
    } catch (error) {
      console.error('Failed to encrypt token, storing unencrypted:', error);
      // Fallback: store unencrypted (less secure but functional)
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.removeItem(this.TOKEN_ENCRYPTED_FLAG);
    }
  }

  /**
   * Retrieve token from localStorage (decrypt if encrypted)
   */
  private async getTokenSecurely(): Promise<string | null> {
    try {
      const encryptedToken = localStorage.getItem(this.TOKEN_KEY);
      if (!encryptedToken) {
        return null;
      }

      // Check if token is encrypted
      const isEncrypted = localStorage.getItem(this.TOKEN_ENCRYPTED_FLAG) === 'true';
      
      if (isEncrypted) {
        try {
          // Decrypt the token
          const decryptedToken = await this.encryptionService.decrypt(
            encryptedToken,
            this.getEncryptionScreenHint()
          );
          // Validate token format (Firebase ID tokens are JWT strings)
          if (decryptedToken && decryptedToken.includes('.')) {
            if (localStorage.getItem(this.ENCRYPTION_V2_KEY) !== '1') {
              void this.storeTokenSecurely(decryptedToken);
            }
            return decryptedToken;
          } else {
            console.warn('Decrypted token appears invalid, trying unencrypted fallback');
            // Fallback: try using stored value as-is (might be unencrypted despite flag)
            return encryptedToken.includes('.') ? encryptedToken : null;
          }
        } catch (decryptError) {
          console.error('Token decryption failed, trying unencrypted fallback:', decryptError);
          // Fallback: if decryption fails, try using stored value as-is
          // This handles cases where encryption might have failed during storage
          return encryptedToken.includes('.') ? encryptedToken : null;
        }
      } else {
        // Legacy unencrypted token - migrate to encrypted
        const token = encryptedToken;
        if (token && token.includes('.')) {
          await this.storeTokenSecurely(token);
          return token;
        }
        return null;
      }
    } catch (error) {
      console.error('Failed to retrieve token:', error);
      return null;
    }
  }

  /**
   * Store refresh token securely (encrypted) in localStorage
   */
  private async storeRefreshTokenSecurely(refreshToken: string): Promise<void> {
    try {
      this.recordEncryptionScreen();
      const encryptedRefreshToken = await this.encryptionService.encrypt(refreshToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, encryptedRefreshToken);
    } catch (error) {
      console.error('Failed to encrypt refresh token, storing unencrypted:', error);
      // Fallback: store unencrypted (less secure but functional)
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Retrieve refresh token from localStorage (decrypt if encrypted)
   */
  private async getRefreshTokenSecurely(): Promise<string | null> {
    try {
      const encryptedRefreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (!encryptedRefreshToken) {
        return null;
      }

      // Try to decrypt (will work for both encrypted and legacy unencrypted)
      try {
        const refreshToken = await this.encryptionService.decrypt(
          encryptedRefreshToken,
          this.getEncryptionScreenHint()
        );
        if (localStorage.getItem(this.ENCRYPTION_V2_KEY) !== '1') {
          void this.storeRefreshTokenSecurely(refreshToken);
        }
        return refreshToken;
      } catch {
        // If decryption fails, it might be unencrypted (legacy)
        // Migrate to encrypted storage
        await this.storeRefreshTokenSecurely(encryptedRefreshToken);
        return encryptedRefreshToken;
      }
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }

  /**
   * Store minimal user data securely (encrypted) in localStorage
   * Only stores non-sensitive data for quick access
   */
  private async storeUserDataSecurely(user: AuthUser): Promise<void> {
    try {
      this.recordEncryptionScreen();
      const minimalData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        isAdmin: user.isAdmin || false,
      };
      const dataString = JSON.stringify(minimalData);
      const encryptedData = await this.encryptionService.encrypt(dataString);
      localStorage.setItem(this.USER_DATA_KEY, encryptedData);
    } catch (error) {
      console.error('Failed to store user data:', error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Get stored user data (decrypted from localStorage)
   * Returns null if data is not available or decryption fails
   */
  private async getStoredUserData(): Promise<AuthUser | null> {
    try {
      const encryptedData = localStorage.getItem(this.USER_DATA_KEY);
      if (!encryptedData) {
        return null;
      }

      // Try to decrypt (will work for both encrypted and legacy unencrypted)
      try {
        const decryptedData = await this.encryptionService.decrypt(
          encryptedData,
          this.getEncryptionScreenHint()
        );
        const userData = JSON.parse(decryptedData) as AuthUser;
        // Migrate to encrypted if it was unencrypted
        await this.storeUserDataSecurely(userData);
        return userData;
      } catch {
        // If decryption fails, it might be unencrypted (legacy)
        try {
          const userData = JSON.parse(encryptedData) as AuthUser;
          // Migrate to encrypted storage
          await this.storeUserDataSecurely(userData);
          return userData;
        } catch {
          return null;
        }
      }
    } catch (error) {
      console.error('Failed to retrieve stored user data:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * Returns true if token exists (even if user profile isn't loaded yet)
   * Note: This is a synchronous check. For full validation, use loadUserProfile()
   * Checks both in-memory token and localStorage (encrypted) token
   */
  isAuthenticated(): boolean {
    const hasToken = this.authToken() !== null || localStorage.getItem(this.TOKEN_KEY) !== null;
    const hasUser = this.currentUser() !== null;
    
    // If we have a token, we're considered authenticated
    // The token will be validated when loadUserProfile() is called
    if (hasToken) {
      // If we have a token but no user yet, try to restore from stored data for quick access
      // Note: getStoredUserData is async, so we can't call it here synchronously
      // The user data will be loaded when needed
      if (hasUser) {
        return true;
      }
      // Token exists, consider authenticated (profile will be loaded separately)
      return true;
    }
    
    // No token - not authenticated
    return false;
  }

  /**
   * Check if authentication is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized();
  }

  /**
   * Check if user is super admin
   */
  isSuperAdmin(): boolean {
    return this.currentUser()?.isAdmin === true;
  }

  /**
   * Whether GET /v1/users (bulk list) is permitted — matches API checks:
   * Firebase isAdmin claim or application role super-admin.
   */
  canBulkListAllUsers(): boolean {
    if (this.currentUser()?.isAdmin === true) {
      return true;
    }
    const profile = this.currentUserProfile();
    const role = profile?.role;
    if (role === undefined || role === null) {
      return false;
    }
    if (role === UserRole.SUPER_ADMIN) {
      return true;
    }
    const roleString = String(role);
    return roleString === 'super-admin' || roleString === 'SUPER_ADMIN';
  }

  /**
   * Check if user is company owner for the active company (or legacy global COMPANY_OWNER role).
   * Ownership also derives from companies.owner_user_id via /companies/:id/context role === 'owner'.
   */
  isCompanyOwner(): boolean {
    const profile = this.currentUserProfile();
    const globalRole = String(profile?.role ?? '').toLowerCase().replace(/_/g, '-');
    if (globalRole === 'company-owner' || profile?.role === 'COMPANY_OWNER') {
      return true;
    }
    const companyRole = String(profile?.companyRole ?? '').toLowerCase();
    return companyRole === 'owner';
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: { name?: string; phoneNumber?: string; profileImage?: string }): Promise<void> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/v1/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`,
        },
        body: JSON.stringify(data),
      });

      const result: AuthResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to update profile');
      }

      // Reload user profile to get updated data
      await this.loadUserProfile();
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/v1/users/me/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const result: AuthResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to change password');
      }
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Handle auth errors
   */
  private handleAuthError(error: any): Error {
    let message = 'An error occurred during authentication';

    if (error instanceof Error) {
      message = error.message;
      if (error.name === 'PasswordResetRateLimitError' || error.name === 'PasswordResetGenericError') {
        return error;
      }
      // Preserve RateLimitError name
      if (error.name === 'RateLimitError') {
        const rateLimitError = new Error(message);
        rateLimitError.name = 'RateLimitError';
        return rateLimitError;
      }
    } else if (typeof error === 'string') {
      message = error;
    }

    // Map common error messages
    if (message.includes('Too many') || message.includes('rate limit') || message.includes('429')) {
      message = message.includes('try again in') ? message : 'Too many login attempts. Please wait a moment and try again.';
      const rateLimitError = new Error(message);
      rateLimitError.name = 'RateLimitError';
      return rateLimitError;
    } else if (message.includes('Invalid email or password') || message.includes('401')) {
      message = 'Invalid email or password';
    } else if (message.includes('Email already registered') || message.includes('already exists')) {
      message = 'This email is already registered';
    } else if (message.includes('Password must be')) {
      message = 'Password must be at least 8 characters';
    } else if (message.includes('network') || message.includes('fetch')) {
      message = 'Network error. Please check your connection';
    }

    return new Error(message);
  }

  /**
   * Get remaining rate limit time in seconds
   * Returns 0 if not rate limited
   */
  getRateLimitRemaining(): number {
    return this.isRateLimited();
  }

  /** Lazy resolve to avoid constructor-time circular dependency with ApiService. */
  private getSessionCleanup(): SessionCleanupService {
    return this.injector.get(SessionCleanupService);
  }
}
